import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import React, { useEffect, useState } from 'react';
import axiosInstance from '../../../utils/axiosInstance';

// dayjs 한국어 설정
dayjs.locale('ko');

// 다크 모드 스타일 상수
const darkModeStyles = {
  container: {
    p: 2,
    background: 'linear-gradient(180deg, rgba(25,25,38,0.95) 0%, rgba(25,32,48,0.95) 100%)',
    minHeight: '100vh',
  },
  paper: {
    p: 3,
    background: 'linear-gradient(145deg, rgba(35,35,50,0.9) 0%, rgba(35,45,65,0.85) 100%)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    backdropFilter: 'blur(8px)',
  },
  title: {
    color: '#fff',
    fontSize: '28px',
    fontWeight: 'bold',
    mb: 1,
    letterSpacing: '0.5px',
    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
  },
  textField: {
    '& .MuiInputBase-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      color: '#fff',
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#60a5fa',
    },
  },
  formControl: {
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      color: '#fff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '& .MuiSelect-icon': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  menuItem: {
    '&.MuiMenuItem-root': {
      color: '#fff',
      backgroundColor: 'rgba(35, 35, 50, 0.95)',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
      '&.Mui-selected': {
        backgroundColor: 'rgba(96, 165, 250, 0.2)',
        '&:hover': {
          backgroundColor: 'rgba(96, 165, 250, 0.3)',
        },
      },
    },
  },
  datePickerTextField: {
    '& .MuiInputBase-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: '#fff',
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)',
    },
    '& .MuiSvgIcon-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  timeInputField: {
    width: '44px',
    '& .MuiInputBase-root': {
      height: '40px',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: '#fff',
    },
    '& .MuiOutlinedInput-input': {
      padding: '4px 6px',
      textAlign: 'center',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.15)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
  },
  searchButton: {
    backgroundColor: '#60a5fa',
    color: '#fff',
    borderRadius: '10px',
    padding: '10px 24px',
    boxShadow: '0 4px 10px rgba(33, 150, 243, 0.3)',
    fontWeight: 'bold',
    textTransform: 'none',
    minWidth: '120px',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#3b82f6',
      boxShadow: '0 6px 12px rgba(30, 136, 229, 0.4)',
      transform: 'translateY(-2px)',
    },
  },
  tableContainer: {
    overflowX: 'auto',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  tableHead: {
    backgroundColor: 'rgba(45, 45, 65, 0.9)',
    '& .MuiTableCell-head': {
      color: '#fff',
      fontWeight: 'bold',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    },
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: 'rgba(255, 255, 255, 0.02)',
    },
    '&:nth-of-type(even)': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      transition: 'background-color 0.3s ease',
    },
    '& .MuiTableCell-body': {
      color: '#fff',
      borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    },
  },
  tableCell: {
    color: '#fff',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
  },
  loadingIndicator: {
    color: '#60a5fa',
    margin: '40px 0',
  },
  noDataMessage: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontStyle: 'italic',
    padding: '40px 0',
  },
  tablePagination: {
    marginTop: '12px',
    borderRadius: '12px',
    backgroundColor: 'rgba(45, 45, 65, 0.8)',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    '& .MuiTablePagination-toolbar': {
      justifyContent: 'center',
    },
    '& .MuiTablePagination-select': {
      padding: '6px 10px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: '#fff',
      fontWeight: 'bold',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
      '& .MuiSelect-icon': {
        color: 'rgba(255, 255, 255, 0.7)',
      },
    },
    '& .MuiTablePagination-actions': {
      color: '#fff',
      '& .MuiIconButton-root': {
        color: 'rgba(255, 255, 255, 0.7)',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
        '&.Mui-disabled': {
          color: 'rgba(255, 255, 255, 0.3)',
        },
      },
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
};

// 수정된 CustomDateTimePicker 컴포넌트 - 다크모드 캘린더/시간 선택기 스타일 적용
const CustomDateTimePicker = ({ label, value, onChange }) => {
  return (
    <DateTimePicker
      label={label}
      value={value}
      onChange={(newValue) => {
        if (!newValue) return;
        console.log(`${label} 변경:`, newValue.format('YYYY-MM-DD HH:mm:ss'));
        onChange(newValue);
      }}
      format="YYYY-MM-DD HH:mm"
      ampm={false}
      slotProps={{
        textField: {
          sx: darkModeStyles.datePickerTextField,
          fullWidth: true,
        },
        popper: {
          sx: {
            '& .MuiPaper-root': {
              backgroundColor: 'rgba(35, 35, 50, 0.95)',
              color: '#fff',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '8px',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
              '& .MuiPickersDay-root': {
                color: '#fff',
                '&.Mui-selected': {
                  backgroundColor: '#60a5fa',
                  color: '#fff',
                },
                '&:hover': {
                  backgroundColor: 'rgba(96, 165, 250, 0.2)',
                },
              },
              '& .MuiDayCalendar-weekDayLabel': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
              '& .MuiPickersCalendarHeader-label': {
                color: '#fff',
              },
              '& .MuiPickersCalendarHeader-switchViewButton': {
                color: '#fff',
              },
              '& .MuiIconButton-root': {
                color: '#fff',
              },
              '& .MuiClock-pin': {
                backgroundColor: '#60a5fa',
              },
              '& .MuiClockPointer-root': {
                backgroundColor: '#60a5fa',
              },
              '& .MuiClockPointer-thumb': {
                border: '14px solid #60a5fa',
                backgroundColor: '#60a5fa',
              },
              '& .MuiClock-clock': {
                backgroundColor: 'rgba(50, 50, 70, 0.95)',
              },
              '& .MuiClockNumber-root': {
                color: '#fff',
              },
              '& .MuiPickersYear-yearButton': {
                color: '#fff',
                '&.Mui-selected': {
                  backgroundColor: '#60a5fa',
                  color: '#fff',
                },
              },
              '& .MuiPickersMonth-root': {
                color: '#fff',
                '&.Mui-selected': {
                  backgroundColor: '#60a5fa',
                },
              },
              '& .MuiPickersArrowSwitcher-button': {
                color: '#fff',
              },
              '& .MuiButtonBase-root': {
                color: '#fff',
              },
              '& .MuiButton-root': {
                color: '#60a5fa',
              },
              '& .MuiPickersDay-today': {
                border: '1px solid #60a5fa',
              },
            },
          },
        },
      }}
    />
  );
};

const UserMoney = () => {
  const isNonMobile = useMediaQuery('(min-width:600px)');
  const [filters, setFilters] = useState({
    agentId: '',
    userId: '',
    userNickname: '',
    // 명시적으로 시간을 포함하여 초기화
    startDate: dayjs().hour(0).minute(0).second(0), // 오늘 00:00:00
    endDate: dayjs().hour(23).minute(59).second(59), // 오늘 23:59:59
    transactionType: 'addsub', // 머니 거래로 고정
  });

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [agents, setAgents] = useState([]); // 에이전트 목록
  const [currentUser, setCurrentUser] = useState(null); // 현재 로그인한 사용자

  // 페이지네이션 상태
  const [page, setPage] = useState(0); // 현재 페이지 번호
  const [rowsPerPage, setRowsPerPage] = useState(30); // 한 페이지에 보여지는 행 개수

  // 현재 로그인한 사용자 정보 가져오기
  const getUser = () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  };

  // 초기 데이터 가져오기
  useEffect(() => {
    const fetchInitialData = async () => {
      // 현재 로그인한 사용자 정보 가져오기
      const user = getUser();
      if (user) {
        setCurrentUser(user);
        if (user.grade !== 'admin') {
          setFilters(prev => ({ ...prev, agentId: user.id }));
        }
      }

      // 에이전트 목록 가져오기 (admin이 아닌 경우에만)
      if (user && user.grade !== 'admin') {
        try {
          const response = await axiosInstance.get('/agent/children');
          if (response.data.success) {
            const agentTree = response.data.agent_tree;
            const agentOptions = [
              { id: agentTree.id, name: agentTree.id, agent_srl: 0, level: 0 }, // 현재 로그인한 에이전트
              ...agentTree.children.map(child => ({
                id: child.id,
                name: child.id,
                level: child.level,
              })),
            ];
            setAgents(agentOptions);
          }
        } catch (error) {
          console.error('에이전트 목록 불러오기 실패:', error);
        }
      }
    };

    fetchInitialData();
  }, []);

  // fetchTransactions 함수 개선
  const fetchTransactions = async () => {
    setLoading(true);
    try {
      // 명시적으로 날짜 포맷팅 - 문자열로 변환 전에 값 확인
      console.log('실제 시작 날짜 객체:', filters.startDate);
      console.log('실제 종료 날짜 객체:', filters.endDate);
      
      // 확실하게 시간까지 포함된 문자열로 포맷팅
      const startDateFormatted = filters.startDate
        ? filters.startDate.format('YYYY-MM-DD HH:mm:ss')
        : dayjs().hour(0).minute(0).second(0).format('YYYY-MM-DD HH:mm:ss');

      const endDateFormatted = filters.endDate
        ? filters.endDate.format('YYYY-MM-DD HH:mm:ss')
        : dayjs().hour(23).minute(59).second(59).format('YYYY-MM-DD HH:mm:ss');

      console.log('요청 시작 시간 (포맷팅 후):', startDateFormatted);
      console.log('요청 종료 시간 (포맷팅 후):', endDateFormatted);

      // POST 요청에 맞게 매개변수 전달
      const requestData = {
        start: startDateFormatted,
        end: endDateFormatted,
        page: page + 1, // API는 1부터 시작하는 페이징
        perPage: rowsPerPage,
        transaction_type: filters.transactionType, // addsub으로 고정
        grade: 'user',
      };

      // 선택적 파라미터 추가
      if (filters.agentId) requestData.agent_id = filters.agentId;
      if (filters.userId) requestData.user_id = filters.userId;
      if (filters.userNickname) requestData.nickname = filters.userNickname;

      // API 요청
      const response = await axiosInstance.post('/transaction/betting', requestData);

      // 응답 데이터 처리
      if (response.data.code === 0) {
        setData(response.data.data || []);
        setTotalCount(response.data.meta.total || 0);
      } else {
        console.error('API 오류:', response.data.message);
        setData([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('트랜잭션 조회 실패:', error);
      setData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // 페이지 또는 페이지당 행 수 변경 시 데이터 다시 가져오기
  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      fetchTransactions();
    }
  }, [page, rowsPerPage]);

  const handleFilterChange = (field, value) => {
    console.log(`필터 변경: ${field}`, value);
    if (field === 'startDate' || field === 'endDate') {
      console.log(`${field} 변경 (포맷팅):`, value ? value.format('YYYY-MM-DD HH:mm:ss') : '값 없음');
    }
    setFilters(prevFilters => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    setPage(0); // 검색 시 첫 페이지로 이동
    console.log('검색 시작 시간:', filters.startDate.format('YYYY-MM-DD HH:mm:ss'));
    console.log('검색 종료 시간:', filters.endDate.format('YYYY-MM-DD HH:mm:ss'));
    fetchTransactions();
  };

  // 페이지 변경 핸들러
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // 보여지는 행 개수 변경 핸들러
  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // 페이지를 첫 번째로 초기화
  };

  // 테이블 행 데이터 변환 함수
  const formatRowData = transaction => {
    // ISO 날짜 형식(2025-04-21T09:32:00.111000Z)을 일반 형식(2025-04-21 09:32:00.111)으로 변환
    let formattedDate = '';
    if (transaction.processing_time) {
      // T와 Z 제거하고 형식 변환
      if (typeof transaction.processing_time === 'string') {
        formattedDate = transaction.processing_time
          .replace('T', ' ') // T를 공백으로 변경
          .replace(/Z$/, ''); // 끝에 있는 Z 제거

        // 밀리초가 6자리(마이크로초)인 경우 3자리(밀리초)로 줄임
        formattedDate = formattedDate.replace(/(\.\d{6})/, match => match.substring(0, 4));
      } else {
        // 문자열이 아닌 경우(이미 dayjs 객체 등으로 변환된 경우)
        formattedDate = transaction.processing_time;
      }
    }

    return {
      transactionId: transaction.trans_id || transaction.id || '',
      userId: transaction.mem_id || '',
      siteId: transaction.site_id || '',
      type: transaction.trans_type || '',
      amount: transaction.amount || 0,
      after: transaction.after_balance || 0,
      processor: transaction.processor_id || '',
      processe_amount: transaction.process_amount || '-',
      post_processe_amount: transaction.post_process_balance || 0,
      processing_time: formattedDate,
    };
  };

  // 숫자 포맷팅 함수
  const formatNumber = number => {
    if (number === null || number === undefined) return '0';
    return number.toLocaleString('ko-KR');
  };

  // 거래 유형에 따른 색상 지정
  const getTypeColor = type => {
    switch (type.toLowerCase()) {
      case 'add':
      case '지급':
        return '#4ade80'; // 녹색
      case 'sub':
      case '회수':
        return '#f87171'; // 빨간색
      default:
        return '#fff'; // 기본 흰색
    }
  };

  return (
    <Box sx={darkModeStyles.container}>
      <Paper sx={darkModeStyles.paper}>
        <Typography variant="h4" sx={darkModeStyles.title}>
          지급 내역
        </Typography>

        {/* 필터 영역 */}
        <Box display="flex" flexDirection={isNonMobile ? 'row' : 'column'} gap={2} sx={{ mb: 3 }}>
          {currentUser && currentUser.grade === 'admin' ? (
            <TextField
              label="에이전트 아이디"
              value={filters.agentId}
              onChange={e => handleFilterChange('agentId', e.target.value)}
              fullWidth
              sx={darkModeStyles.textField}
            />
          ) : (
            <FormControl fullWidth sx={darkModeStyles.formControl}>
              <InputLabel>에이전트 아이디</InputLabel>
              <Select
                value={filters.agentId}
                onChange={e => handleFilterChange('agentId', e.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: 'rgba(35, 35, 50, 0.95)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                    },
                  },
                }}
              >
                {agents.map(agent => (
                  <MenuItem
                    key={agent.id}
                    value={agent.id}
                    sx={{
                      ...darkModeStyles.menuItem,
                      backgroundColor: agent.level === 0 ? 'rgba(96, 165, 250, 0.1)' : 'inherit',
                      fontWeight: agent.level === 0 ? 600 : 400,
                      '&:hover': {
                        backgroundColor: agent.level === 0 ? 'rgba(96, 165, 250, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: agent.level === 0 ? 'rgba(96, 165, 250, 0.3)' : 'rgba(96, 165, 250, 0.2)',
                      },
                    }}
                  >
                    {agent.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            label="유저 아이디"
            value={filters.userId}
            onChange={e => handleFilterChange('userId', e.target.value)}
            fullWidth
            sx={darkModeStyles.textField}
          />

          <TextField
            label="유저 닉네임"
            value={filters.userNickname}
            onChange={e => handleFilterChange('userNickname', e.target.value)}
            fullWidth
            sx={darkModeStyles.textField}
          />
        </Box>
        <Box display="flex" flexDirection={isNonMobile ? 'row' : 'column'} gap={2} sx={{ mb: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ flex: 1, display: 'flex', gap: 2, flexDirection: isNonMobile ? 'row' : 'column' }}>
              <CustomDateTimePicker label="시작 날짜 및 시간" value={filters.startDate} onChange={date => handleFilterChange('startDate', date)} />
              <CustomDateTimePicker label="종료 날짜 및 시간" value={filters.endDate} onChange={date => handleFilterChange('endDate', date)} />
            </Box>
          </LocalizationProvider>

          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading}
            sx={{
              ...darkModeStyles.searchButton,
              height: isNonMobile ? 'auto' : 56,
              alignSelf: isNonMobile ? 'flex-start' : 'stretch',
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon sx={{ fontSize: 20, mr: 1 }} />}
            {loading ? '로딩 중...' : '검색'}
          </Button>
        </Box>

        {/* 테이블 영역 */}
        <TableContainer sx={darkModeStyles.tableContainer}>
          <Table stickyHeader>
            <TableHead sx={darkModeStyles.tableHead}>
              <TableRow>
                <TableCell sx={darkModeStyles.tableCell}>트랜잭션 번호</TableCell>
                <TableCell sx={darkModeStyles.tableCell}>유저</TableCell>
                <TableCell sx={darkModeStyles.tableCell}>사이트 아이디</TableCell>
                <TableCell sx={darkModeStyles.tableCell}>타입</TableCell>
                <TableCell sx={darkModeStyles.tableCell}>포인트</TableCell>
                <TableCell sx={darkModeStyles.tableCell}>이후 포인트</TableCell>
                <TableCell sx={darkModeStyles.tableCell}>처리자</TableCell>
                <TableCell sx={darkModeStyles.tableCell}>처리 포인트</TableCell>
                <TableCell sx={darkModeStyles.tableCell}>처리 이후 포인트</TableCell>
                <TableCell sx={darkModeStyles.tableCell}>처리 시간</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress size={40} sx={darkModeStyles.loadingIndicator} />
                    <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
                      데이터를 불러오는 중...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" sx={darkModeStyles.noDataMessage}>
                      데이터가 없습니다.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, index) => {
                  const formattedRow = formatRowData(row);
                  return (
                    <TableRow key={index} sx={darkModeStyles.tableRow}>
                      <TableCell sx={darkModeStyles.tableCell}>{formattedRow.transactionId}</TableCell>
                      <TableCell sx={darkModeStyles.tableCell}>{formattedRow.userId}</TableCell>
                      <TableCell sx={darkModeStyles.tableCell}>{formattedRow.siteId}</TableCell>
                      <TableCell
                        sx={{
                          ...darkModeStyles.tableCell,
                          color: getTypeColor(formattedRow.type),
                          fontWeight: 'bold',
                        }}
                      >
                        {formattedRow.type}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...darkModeStyles.tableCell,
                          color: formattedRow.amount >= 0 ? '#4ade80' : '#f87171',
                          fontWeight: 'bold',
                        }}
                      >
                        {formatNumber(formattedRow.amount)}
                      </TableCell>
                      <TableCell sx={darkModeStyles.tableCell}>{formatNumber(formattedRow.after)}</TableCell>
                      <TableCell sx={darkModeStyles.tableCell}>{formattedRow.processor}</TableCell>
                      <TableCell
                        sx={{
                          ...darkModeStyles.tableCell,
                          color: formattedRow.processe_amount >= 0 ? '#4ade80' : '#f87171',
                          fontWeight: formattedRow.processe_amount !== '-' ? 'bold' : 'normal',
                        }}
                      >
                        {formattedRow.processe_amount !== '-' ? formatNumber(formattedRow.processe_amount) : '-'}
                      </TableCell>
                      <TableCell sx={darkModeStyles.tableCell}>{formatNumber(formattedRow.post_processe_amount)}</TableCell>
                      <TableCell sx={darkModeStyles.tableCell}>{formattedRow.processing_time}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          {!loading && data.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[30, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="페이지당 행 수"
              sx={darkModeStyles.tablePagination}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: 'rgba(35, 35, 50, 0.95)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '8px',
                      '& .MuiMenuItem-root': {
                        color: '#fff',
                        padding: '8px 16px',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(96, 165, 250, 0.2)',
                        },
                      },
                    },
                  },
                },
              }}
            />
          )}
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default UserMoney;